import React, { useState } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Col } from '@amzn/stencil-react-components/layout'
import { Modal } from '@amzn/stencil-react-components/modal'

import { GALLERY_ITEM_TYPES } from 'src/components/LookAndFeelGallery/galleryItemTypes'
import { LookAndFeelGallery } from 'src/components/LookAndFeelGallery/LookAndFeelGallery'
import { ModalContent } from 'src/components/ModalContent'
import { getItemTitle, ItemType } from 'src/models/dto/items/ItemDTO'

export interface AddFromGalleryModalProps {
    /** Whether the modal is open; the parent (e.g. a page-options menu item) controls this. */
    isOpen: boolean
    /** Called when the modal should close (Close button or after a successful insert). */
    onClose: () => void
    /** Entity id of the module being edited. */
    moduleEntityId: string
    /**
     * When set, the picked page's items and context box are merged into this existing page instead
     * of being added as a new page.
     */
    targetActivityId?: string
    /** Workflow position to insert a new page at (ignored in merge mode). */
    pageIndex?: number
}

/**
 * A controlled, two-step gallery picker intended for entry points that open from a menu (e.g. the
 * page-options "Add from Gallery"). Step one chooses an item type; step two shows the
 * {@link LookAndFeelGallery} for that type. When `targetActivityId` is provided the chosen page is
 * merged into that page rather than added as a new page.
 */
export const AddFromGalleryModal = ({
    isOpen,
    onClose,
    moduleEntityId,
    targetActivityId,
    pageIndex,
}: AddFromGalleryModalProps) => {
    const [selectedItemType, setSelectedItemType] = useState<ItemType | null>(null)

    const close = () => {
        setSelectedItemType(null)
        onClose()
    }

    const buttons = [
        selectedItemType && (
            <Button
                key='back-to-item-types'
                dataTestId='gallery-item-type-back'
                variant={ButtonVariant.Secondary}
                onClick={() => setSelectedItemType(null)}
            >
                Back
            </Button>
        ),
        <Button key='close-add-from-gallery-modal' onClick={close}>
            Close
        </Button>,
    ].filter(Boolean)

    return (
        <Modal isOpen={isOpen} close={close}>
            <ModalContent
                titleText={selectedItemType ? 'Pick a look-and-feel' : 'Choose an item type'}
                maxWidth='1100px'
                buttons={buttons}
            >
                {selectedItemType ? (
                    <LookAndFeelGallery
                        itemType={selectedItemType}
                        moduleEntityId={moduleEntityId}
                        targetActivityId={targetActivityId}
                        pageIndex={pageIndex}
                        onInserted={close}
                    />
                ) : (
                    <Col gridGap='S300' padding='S300'>
                        {GALLERY_ITEM_TYPES.map((itemType) => (
                            <Button
                                key={itemType}
                                dataTestId={`gallery-item-type-${itemType}-option`}
                                variant={ButtonVariant.Tertiary}
                                onClick={() => setSelectedItemType(itemType)}
                            >
                                {getItemTitle(itemType)}
                            </Button>
                        ))}
                    </Col>
                )}
            </ModalContent>
        </Modal>
    )
}
